import React, { useState } from 'react';
import ValidatedList from './ValidatedList';
import { UploadOutlined } from '@ant-design/icons';
import { Card, Select, Upload, Button, Row, Col, message, Typography, Progress, Modal } from 'antd';
import { validatePayments } from '../../services/banks';
import moment from 'moment';
import XLSX from 'xlsx';
import { useWindowDimensions } from '../../utils/hooks';
import { State, States } from 'sigt';
import { connect } from 'react-redux';

const { Option } = Select;

// Constante para el tamaño máximo de lote (límite del servidor)
const BATCH_SIZE = 100;

// Función para dividir array en chunks
const chunkArray = <T,>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

// Interface para estadísticas acumuladas de lotes
interface BatchStats {
  validatedCount: number;
  totalReceived: number;
  notFoundCount: number;
  alreadyApprovedCount: number;
  duplicatesCount: number;
  failedCount: number;
  failedBatches: number;
  allData: any[];
}

const ValidatePayments: React.FC<ValidatePaymentsProps> = ({ thm, banks }) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [bank, setBank] = useState(1);
  const [listData, setListData] = useState<any[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const { width } = useWindowDimensions();

  // Función principal para procesar pagos en lotes
  const processBatches = async (json: any[]) => {
    const batches = chunkArray(json, BATCH_SIZE);
    const totalBatches = batches.length;

    // Inicializar estadísticas
    const stats: BatchStats = {
      validatedCount: 0,
      totalReceived: 0,
      notFoundCount: 0,
      alreadyApprovedCount: 0,
      duplicatesCount: 0,
      failedCount: 0,
      failedBatches: 0,
      allData: [],
    };

    // Mostrar modal de progreso
    setLoading(true);
    setProgress(0);
    setProgressMessage(`Procesando ${json.length} pagos en ${totalBatches} lote(s)...`);

    // Procesar cada lote secuencialmente
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];

      // Actualizar mensaje de progreso
      setProgressMessage(`Procesando lote ${i + 1} de ${totalBatches}...`);

      try {
        // Enviar lote al servidor
        const response = await validatePayments({ bank, data: batch });
        const result = response.validatePayments;

        // Acumular estadísticas
        stats.validatedCount += result.validatedCount || 0;
        stats.totalReceived += result.totalReceived || batch.length;
        stats.notFoundCount += result.notFound?.length || 0;
        stats.alreadyApprovedCount += result.alreadyApproved?.length || 0;
        stats.duplicatesCount += result.duplicates?.length || 0;
        stats.failedCount += result.failed?.length || 0;
        stats.allData = [...stats.allData, ...(result.data || [])];
      } catch (error) {
        // Si un lote falla, continuar con los demás
        stats.failedBatches++;
        stats.totalReceived += batch.length;
        console.error(`Error en lote ${i + 1}:`, error);
      }

      // Actualizar progreso
      setProgress(Math.round(((i + 1) / totalBatches) * 100));
    }

    // Construir mensaje resumen
    const parts: string[] = [];
    if (stats.validatedCount > 0) parts.push(`${stats.validatedCount} validados`);
    if (stats.alreadyApprovedCount > 0) parts.push(`${stats.alreadyApprovedCount} ya aprobados`);
    if (stats.notFoundCount > 0) parts.push(`${stats.notFoundCount} no encontrados`);
    if (stats.duplicatesCount > 0) parts.push(`${stats.duplicatesCount} duplicados`);
    if (stats.failedCount > 0) parts.push(`${stats.failedCount} fallidos`);
    if (stats.failedBatches > 0) parts.push(`${stats.failedBatches} lote(s) con error`);

    const summaryMessage = `De ${stats.totalReceived} pagos: ${parts.length > 0 ? parts.join(', ') : 'ninguno procesado'}`;

    // Mostrar mensaje de completado
    setProgressMessage(summaryMessage);
    setProgress(100);

    // Mostrar mensaje final con duración de 10 segundos
    if (stats.failedBatches > 0 || stats.failedCount > 0) {
      message.warning({ content: summaryMessage, key: 'batch-upload', duration: 10 });
    } else {
      message.success({ content: summaryMessage, key: 'batch-upload', duration: 10 });
    }

    // Actualizar estado con todos los datos acumulados
    if (stats.allData.length > 0) {
      setListData([...listData, ...stats.allData]);
    }
    setFileList([]);

    // Cerrar modal automáticamente después de 3 segundos
    setTimeout(() => {
      setLoading(false);
      setProgress(0);
      setProgressMessage('');
    }, 3000);
  };

  const ExcelDateToMomentJS = (serial) => {
    // let days = Math.floor(serial);
    let hours = Math.floor((serial % 1) * 24);
    let minutes = Math.floor(((serial % 1) * 24 - hours) * 60);
    const full_date = new Date(Date.UTC(0, 0, serial, hours - 20, minutes));
    return moment(full_date).toISOString();
  };

  const handleUpload = async () => {
    const reader = new FileReader();
    let json: [] | any;
    let error: string | undefined;

    if (bank === 1) {
      reader.onload = function (event: any) {
        let fileContent: any;
        json = [];
        fileContent = event.target.result;
        //BOD
        try {
          if (fileList[0].type === 'application/json') {
            const enc = new TextDecoder('utf-8');
            json = JSON.parse(enc.decode(fileContent));
            //first Out : bank account data
            json.shift();
            if ('Referencia' in json[0] && 'Monto' in json[0] && 'Fecha' in json[0]) {
              json.forEach((e) => {
                e.Fecha = moment(e.Fecha, 'DD/MM/YYYY').toISOString();
              });
            } else {
              json = [];
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          } else {
            let arr = fileContent.split('\n');
            arr = arr.map((e) => e.split(','));
            arr.forEach((row) => {
              if (row.length === 7) {
                json.push({
                  Fecha: moment(row[0], 'DD-M-YYYY').toISOString(),
                  Referencia: row[2],
                  Descripcion: row[3],
                  Monto: parseFloat(row[4]),
                });
              }
            });
          }
        } catch (err) {
          error = 'El archivo cargado no es el esperado o ocurrio un error leyendo el archivo.';
        }

        if (!error) {
          processBatches(json);
        } else {
          message.error(error);
        }
      };
      if (fileList[0]?.type === 'application/json') {
        reader.readAsArrayBuffer(fileList[0]);
      } else {
        reader.readAsText(fileList[0]);
      }
    } else {
      reader.onload = function (event: any) {
        let buffer: any;
        buffer = event.target.result;
        let error: string | undefined;
        json = [];
        try {
          //raw is used to keep all data as string
          let data = XLSX.read(buffer, { type: 'buffer', raw: true });
          let arr: any[] = XLSX.utils.sheet_to_json(data.Sheets[data.SheetNames[0]], {
            header: 1,
          });

          //a
          //BANESCO
          if (bank === 39) {
            if (arr[0].includes('Referencia') && arr[0].includes('Monto') && arr[0].includes('Fecha')) {
              for (let i = 1; i < arr.length; i++) {
                if (!arr[i].includes('Referencia') && !arr[i].includes('Monto') && !arr[i].includes('Fecha')) {
                  json.push({
                    Fecha: ExcelDateToMomentJS(arr[i][0]), //date object
                    Referencia: arr[i][1],
                    Descripcion: arr[i][2],
                    Monto: arr[i][3],
                  });
                }
              }
            } else {
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          }

          //BNC
          if (bank === 3) {
            if (arr[15][1].includes('Fecha') && arr[15][12].includes('Referencia') && arr[15][15].includes('Haber')) {
              for (let i = 16; i < arr.length; i++) {
                json.push({
                  Fecha: ExcelDateToMomentJS(arr[i][1]), //date object
                  Referencia: arr[i][12],
                  Monto: arr[i][16],
                });
              }
            } else {
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          }

          //BANCO DEL TESORO
          if (bank === 14) {
            if (arr[0][0].includes('Fecha') && arr[0][2].includes('Referencia') && arr[0][4].includes('Abono')) {
              for (let i = 1; i < arr.length; i++) {
                if (arr[i][2] !== 0) {
                  json.push({
                    Fecha: ExcelDateToMomentJS(arr[i][0]), //date object
                    Referencia: `${arr[i][2]}`,
                    Monto: arr[i][4],
                  });
                }
              }
            } else {
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          }

          //BANCO PLAZA
          if (bank === 22) {
            if (arr[1][0].includes('Fecha') && arr[1][1].includes('Referencia') && arr[1][4].includes('Abono')) {
              for (let i = 2; i < arr.length; i++) {
                const Monto = parseFloat(arr[i][4].replace(/\./g, '').replace(/,/, '.'));
                json.push({
                  Fecha: moment(arr[i][0], 'DD/MM/YYYY').toISOString(), //date object
                  Referencia: arr[i][1],
                  Monto,
                });
              }
            } else {
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          }

          //BANCRECER
          if (bank === 25) {
            if (arr[1][0].includes('FECHA') && arr[1][1].includes('REFERENCIA') && arr[1][4].includes('CREDITOS')) {
              for (let i = 2; i < arr.length; i++) {
                if (!!arr[i][4]) {
                  json.push({
                    Fecha: moment(arr[i][0], 'YYYY-MM-DD').toISOString(), //date object
                    Referencia: `${arr[i][1]}`,
                    Monto: parseFloat(arr[i][4]?.replace(/\./g, '')?.replace(/,/, '.')),
                  });
                }
              }
            } else {
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          }

          // MERCANTIL Y PROVINCIAL
          if (bank === 19 || bank === 23) {
            if (arr[0][0] === 'REFERENCIA' && arr[0][2] === 'MONTO' && arr[0][1] === 'FECHA') {
              for (let i = 1; i < arr.length; i++) {
                if (arr[i].length > 2 && !isNaN(arr[i][2])) {
                  json.push({
                    Fecha: ExcelDateToMomentJS(arr[i][1]), //date object
                    Referencia: `${arr[i][0]}`,
                    Monto: arr[i][2] * 1, // Lo paso a positivo para evitar cualquier monto negativo
                  });
                }
              }
            } else {
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          }
          // VENEZUELA
          if (bank === 11) {
            if (arr[0][0] === 'fecha' && arr[0][1] === 'referencia' && arr[0][4] === 'monto') {
              for (let i = 1; i < arr.length; i++) {
                // Filtrar filas vacías: verificar que fecha, referencia y monto existan
                if (!arr[i][0] || !arr[i][1] || !arr[i][4]) {
                  continue;
                }
                // let newMonto = String(arr[i][5]).replaceAll('.', '');
                let newMonto = String(arr[i][4]).split('.').join('');  
                newMonto.replace(',','.');
                json.push({
                  Fecha: moment(arr[i][0], 'DD/MM/YYYY').startOf('day').toISOString(), //date object
                  Referencia: arr[i][1],
                  Monto: parseFloat(newMonto),
                });
              }
            } else {
              error = 'El archivo cargado no contiene los datos esperados!';
            }
          }

          //BANCARIBE
          if (bank === 12) {
            for (let i = 1; i < arr.length; i++) {
              if (typeof arr[i][0] === 'number' && typeof arr[i][4] === 'number') {
                json.push({
                  Fecha: ExcelDateToMomentJS(arr[i][0]), //date object
                  Referencia: `${arr[i][1]}`,
                  Monto: arr[i][4],
                });
              }
            }
          }
        } catch (err) {
          json = [];
          error = 'El archivo cargado no es el esperado.';
        }

        if (!error) {
          processBatches(json);
        } else {
          message.error(error);
        }
      };
      reader.readAsArrayBuffer(fileList[0]);
    }
  };

  const excelTypes = [
    'application/vnd.ms-excel',
    'application/excel',
    'application/vnd.ms-excel',
    'application/x-excel',
    'application/x-msexcel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file: Blob) => {
      const BODFileTypes = ['application/vnd.ms-excel', 'text/csv', 'application/json'];
      if (bank !== 1 && !excelTypes.includes(file.type)) {
        message.error('Solo son aceptados los archivos tipo XLS o XLSX');
      } else if (bank === 1 && !BODFileTypes.includes(file.type)) {
        message.error('Solo son aceptados los archivos tipo csv o json');
      }
      setFileList([file]);
      return false;
    },
    accept: bank === 1 ? '.csv, .json' : '.xls,.xlsx',
    fileList,
  };

  return (
    <Card
      style={{ height: '100%' }}
      title='Validación de Pagos'
      bodyStyle={{ height: 'calc(100% - 88px)', overflowY: 'scroll', overflowX: 'hidden' }}
      headStyle={{ height: 64, backgroundColor: thm.primaryColor, padding: width < 992 ? '0 10px' : '0 20px', color: 'white' }}
    >
      <Row gutter={[8, 16]}>
        <Col xs={24} sm={12} md={7}>
          <Select
            placeholder='Seleccione banco'
            onChange={(bank: number) => {
              setBank(bank);
              setFileList([]);
            }}
            // defaultValue={1}
            style={{ width: '100%' }}
          >
            {banks
              .filter((b) => b.validador)
              .map((b) => (
                <Option key={b.id} value={b.id}>
                  {b.nombre}
                </Option>
              ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Button style={{ width: '100%' }} type='primary' onClick={handleUpload} disabled={typeof fileList[0] === 'undefined'}>
            Validar Pagos
          </Button>
        </Col>
      </Row>
      <Row gutter={[8, 40]}>
        <Col xs={24} md={12}>
          <Upload {...uploadProps}>
            <Button disabled={typeof fileList[0] !== 'undefined'}>
              <UploadOutlined /> Subir Archivo
            </Button>
          </Upload>
        </Col>
      </Row>
      <Row gutter={[8, 24]}>
        <Col span={24}>
          <Typography.Title level={4}>Pagos validados</Typography.Title>
          <ValidatedList listData={listData} />
        </Col>
      </Row>
      <Modal
        visible={loading}
        title='Validando pagos'
        closable={false}
        footer={null}
        centered
        maskClosable={false}
      >
        <div style={{ textAlign: 'center' }}>
          <Progress percent={progress} status={progress < 100 ? 'active' : 'success'} />
          <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
            {progressMessage}
          </Typography.Text>
        </div>
      </Modal>
    </Card>
  );
};

const mapStateToProps = (state: State) => ({ thm: state.thm, banks: state.bk.banks });

export default connect(mapStateToProps)(ValidatePayments);

interface ValidatePaymentsProps {
  thm: States.ThemeColors;
  banks: States.Banks['banks'];
}
